import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customerCheckoutLinksService } from '@/services/provider/customer-checkout-links.service';
import type {
  ProviderCheckoutLinkCreateInput,
  ProviderCheckoutLinkCreateResult,
  ProviderCheckoutLink,
  ProviderCheckoutLinkListResponse,
} from '@/types/provider-checkout-links';

export const providerCheckoutLinkKeys = {
  all: ['provider', 'customers', 'checkout-links'] as const,
  list: (customerId: string) => [...providerCheckoutLinkKeys.all, customerId] as const,
  options: (customerId: string, checkoutType?: string | null) =>
    [...providerCheckoutLinkKeys.all, customerId, 'options', checkoutType ?? null] as const,
};

export function useProviderCustomerCheckoutLinks(customerId: string | undefined) {
  return useQuery<ProviderCheckoutLinkListResponse>({
    queryKey: customerId ? providerCheckoutLinkKeys.list(customerId) : providerCheckoutLinkKeys.all,
    queryFn: () => {
      if (!customerId) {
        throw new Error('Missing customer id');
      }
      return customerCheckoutLinksService.list(customerId);
    },
    enabled: Boolean(customerId),
  });
}

export function useGenerateCustomerCheckoutLink() {
  const queryClient = useQueryClient();

  return useMutation<
    ProviderCheckoutLinkCreateResult,
    unknown,
    { customerId: string; input: ProviderCheckoutLinkCreateInput }
  >({
    mutationFn: ({ customerId, input }) =>
      customerCheckoutLinksService.create(customerId, input),
    onSuccess: (data, variables) => {
      const createdAt = new Date().toISOString();
      const expiresAt = data.expiresAt ?? variables.input.expiresAt ?? null;
      const newLink: ProviderCheckoutLink = {
        id: `local-${Date.now()}`,
        productId: variables.input.productId,
        planId: variables.input.planId ?? null,
        packageId: variables.input.packageId ?? null,
        cycleId: variables.input.cycleId ?? null,
        priceId: variables.input.priceId ?? null,
        currency: variables.input.currency ?? null,
        status: 'active',
        createdAt,
        expiresAt,
        usedAt: null,
      };
      queryClient.setQueryData<ProviderCheckoutLinkListResponse>(
        providerCheckoutLinkKeys.list(variables.customerId),
        (existing) => ({
          checkoutLinks: [newLink, ...(existing?.checkoutLinks ?? [])],
        }),
      );
      queryClient.invalidateQueries({
        queryKey: providerCheckoutLinkKeys.list(variables.customerId),
      });
    },
  });
}

export function useProviderCustomerCheckoutLinkOptions(
  customerId: string | undefined,
  checkoutType?: 'subscription' | 'package' | null,
) {
  return useQuery({
    queryKey: customerId
      ? providerCheckoutLinkKeys.options(customerId, checkoutType)
      : providerCheckoutLinkKeys.all,
    queryFn: () => {
      if (!customerId) {
        throw new Error('Missing customer id');
      }
      return customerCheckoutLinksService.options(customerId, checkoutType ?? undefined);
    },
    enabled: Boolean(customerId),
  });
}
